/**
 * Native durability mirror for the offline submission queue.
 *
 * IndexedDB is best-effort storage — the browser (and WKWebView in particular)
 * may evict it under pressure or after a no-interaction window, which would lose
 * any record captured offline that hasn't synced yet. `navigator.storage.persist()`
 * only *asks*; it can be denied. The only way to actually GUARANTEE durability is
 * to store outside the browser sandbox.
 *
 * This module does exactly that on the Capacitor native app, WITHOUT changing how
 * the app works day to day:
 *   - IndexedDB/Dexie stays the working store and source of truth at runtime.
 *   - On native only, we additionally write the at-risk write-queue data
 *     (submission payloads + photo blobs + audio sessions/chunk blobs) to the
 *     OS app sandbox via @capacitor/filesystem (Directory.Data — private,
 *     persistent, not evictable by the browser engine).
 *   - On boot we rehydrate any rows the mirror has that IndexedDB is missing
 *     (i.e. IndexedDB was evicted), so the sync loop can then drain them.
 *
 * Everything here is a NO-OP on the web (Capacitor.isNativePlatform() === false),
 * so the browser experience is byte-for-byte unchanged. Every operation is
 * defensive: a mirror failure is logged and swallowed — it must never break an
 * enqueue, a sync, or app startup, because IndexedDB remains the live store.
 */

import { Capacitor } from "@capacitor/core";
import { offlineDb } from "./db";
import type { LocalAudioChunk, LocalAudioSession } from "./db";
import type { QueuedSubmission, QueuedAttachment, FormDraft, StagedJobFile } from "./types";

const ROOT = "offline-mirror";
const SUBS_DIR = `${ROOT}/submissions`;
const ATT_DIR = `${ROOT}/attachments`;
const DRAFTS_DIR = `${ROOT}/drafts`;
const AUDIO_SESS_DIR = `${ROOT}/audio-sessions`;
const AUDIO_CHUNK_DIR = `${ROOT}/audio-chunks`;
const JOBFILE_DIR = `${ROOT}/job-files`;
const ATTACH_PREFIX = "local://attach_";

/**
 * A draftId is `draft:<formUrl>[:job:<jobId>]` — it contains colons (and a
 * formUrl could in principle contain a slash), neither of which is safe in a
 * filename on the native filesystem. Encode it for the mirror path; the draft's
 * own `draftId` is stored inside the JSON, so rehydrate never parses the name.
 */
function draftFileName(draftId: string): string {
  return `${encodeURIComponent(draftId)}.json`;
}

type MirroredAttachment = QueuedAttachment;

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/** Lazily load the plugin so the web bundle never touches it. */
async function fsModule() {
  return import("@capacitor/filesystem");
}

async function ensureDir(
  Filesystem: typeof import("@capacitor/filesystem").Filesystem,
  Directory: typeof import("@capacitor/filesystem").Directory,
  path: string,
): Promise<void> {
  try {
    await Filesystem.mkdir({ path, directory: Directory.Data, recursive: true });
  } catch {
    // already exists — fine.
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  // Chunk to keep the call stack + intermediate strings sane for larger photos.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/** Pull the staged-attachment ids out of a submission payload's `local://` refs. */
function attachmentIdsInPayload(payload: Record<string, unknown>): string[] {
  const ids: string[] = [];
  for (const raw of Object.values(payload)) {
    if (typeof raw !== "string" || raw.length === 0) continue;
    for (const part of raw.split(",").map((s) => s.trim())) {
      if (part.startsWith(ATTACH_PREFIX)) ids.push(part.slice(ATTACH_PREFIX.length));
    }
  }
  return ids;
}

// ── Write path ─────────────────────────────────────────────────────────────

/** Mirror a queued submission's payload + metadata to the native sandbox. */
export async function mirrorSubmission(submission: QueuedSubmission): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    await ensureDir(Filesystem, Directory, SUBS_DIR);
    await Filesystem.writeFile({
      path: `${SUBS_DIR}/${submission.localSubmissionId}.json`,
      data: JSON.stringify(submission),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  } catch (err) {
    console.warn("[native-mirror] mirrorSubmission failed (non-fatal):", err);
  }
}

/** Mirror a staged photo blob (+ sidecar metadata) to the native sandbox. */
export async function mirrorAttachment(attachment: MirroredAttachment): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    await ensureDir(Filesystem, Directory, ATT_DIR);
    const id = attachment.localAttachmentId;
    const base64 = await blobToBase64(attachment.blob);
    await Filesystem.writeFile({
      path: `${ATT_DIR}/${id}.bin`,
      data: base64, // no encoding ⇒ written as base64 binary
      directory: Directory.Data,
    });
    const meta = {
      localAttachmentId: id,
      localSubmissionId: attachment.localSubmissionId,
      filename: attachment.filename,
      mimeType: attachment.mimeType,
      size: attachment.size,
      formUrl: attachment.formUrl,
      fieldId: attachment.fieldId,
      jobId: attachment.jobId ?? null,
      createdAt: attachment.createdAt,
    };
    await Filesystem.writeFile({
      path: `${ATT_DIR}/${id}.meta.json`,
      data: JSON.stringify(meta),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  } catch (err) {
    console.warn("[native-mirror] mirrorAttachment failed (non-fatal):", err);
  }
}

// ── Cleanup path ─────────────────────────────────────────────────────────────

/**
 * Remove a submission's mirror (and the mirrored blobs it references) once the
 * IndexedDB row is being deleted — i.e. reconcile-confirmed synced. Keeps the
 * mirror in lockstep with the live queue.
 */
export async function removeMirroredSubmission(
  submission: QueuedSubmission,
): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory } = await fsModule();
    const del = (path: string) =>
      Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
    await del(`${SUBS_DIR}/${submission.localSubmissionId}.json`);
    for (const id of attachmentIdsInPayload(submission.payloadJson)) {
      await del(`${ATT_DIR}/${id}.bin`);
      await del(`${ATT_DIR}/${id}.meta.json`);
    }
  } catch (err) {
    console.warn("[native-mirror] removeMirroredSubmission failed (non-fatal):", err);
  }
}

// ── Job-file (offline photo) mirror ──────────────────────────────────────────

/**
 * Mirror a staged job photo (+ sidecar metadata) to the native sandbox so it
 * survives IndexedDB eviction before it can upload. A photo captured on a
 * mountainside is the most painful thing to lose; this is the durability
 * backstop. No-op on web; never throws.
 */
export async function mirrorJobFile(staged: StagedJobFile): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    await ensureDir(Filesystem, Directory, JOBFILE_DIR);
    const id = staged.stagedFileId;
    const base64 = await blobToBase64(staged.blob);
    await Filesystem.writeFile({
      path: `${JOBFILE_DIR}/${id}.bin`,
      data: base64,
      directory: Directory.Data,
    });
    const { blob: _blob, ...meta } = staged;
    await Filesystem.writeFile({
      path: `${JOBFILE_DIR}/${id}.meta.json`,
      data: JSON.stringify(meta),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  } catch (err) {
    console.warn("[native-mirror] mirrorJobFile failed (non-fatal):", err);
  }
}

/** Drop a job file's mirror once its IndexedDB row is deleted (reconcile-confirmed). */
export async function removeMirroredJobFile(stagedFileId: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory } = await fsModule();
    const del = (path: string) =>
      Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
    await del(`${JOBFILE_DIR}/${stagedFileId}.bin`);
    await del(`${JOBFILE_DIR}/${stagedFileId}.meta.json`);
  } catch (err) {
    console.warn("[native-mirror] removeMirroredJobFile failed (non-fatal):", err);
  }
}

/**
 * Restore staged job files that IndexedDB has lost to eviction. Runs on field-
 * shell startup before the first outbox drain, so the job.file.index sender
 * finds the blob rather than dead-lettering. No-op on web.
 */
export async function rehydrateJobFilesFromMirror(): Promise<number> {
  if (!isNative()) return 0;
  let restored = 0;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    for (const file of await listDir(Filesystem, Directory, JOBFILE_DIR)) {
      if (!file.endsWith(".meta.json")) continue;
      const id = file.slice(0, -".meta.json".length);
      if (await offlineDb.stagedJobFiles.get(id)) continue; // intact
      try {
        const metaRaw = await Filesystem.readFile({
          path: `${JOBFILE_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const meta = JSON.parse(metaRaw.data as string) as Omit<StagedJobFile, "blob">;
        const binRaw = await Filesystem.readFile({
          path: `${JOBFILE_DIR}/${id}.bin`,
          directory: Directory.Data,
        });
        await offlineDb.stagedJobFiles.put({
          ...meta,
          blob: base64ToBlob(binRaw.data as string, meta.contentType),
        });
        restored++;
      } catch (err) {
        console.warn(`[native-mirror] could not restore job file ${id}:`, err);
      }
    }
  } catch (err) {
    console.warn("[native-mirror] rehydrateJobFilesFromMirror failed (non-fatal):", err);
  }
  return restored;
}

/**
 * Drop a single staged photo's mirror when its IndexedDB row is deleted —
 * uploaded-and-submitted, or swept as an orphan. Without this the mirror is the
 * source of truth the next cold start restores from (`rehydrateFromMirror`
 * re-puts any `.meta.json` with no live row), so a deleted blob would come back
 * from the dead and be counted as pending all over again.
 */
export async function removeMirroredAttachment(
  localAttachmentId: string,
): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory } = await fsModule();
    const del = (path: string) =>
      Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
    await del(`${ATT_DIR}/${localAttachmentId}.bin`);
    await del(`${ATT_DIR}/${localAttachmentId}.meta.json`);
  } catch (err) {
    console.warn("[native-mirror] removeMirroredAttachment failed (non-fatal):", err);
  }
}

// ── Draft write path ───────────────────────────────────────────────────────

/**
 * Mirror an in-progress form draft to the native sandbox. Called on every
 * (debounced) draft save so a half-filled form — text answers plus the aerial
 * snapshot's `local://attach_` reference — survives IndexedDB eviction, not just
 * an ordinary close/reopen. The referenced photo/aerial blobs are mirrored
 * separately by `mirrorAttachment`, so a rehydrated draft finds them intact.
 */
export async function mirrorDraft(draft: FormDraft): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    await ensureDir(Filesystem, Directory, DRAFTS_DIR);
    await Filesystem.writeFile({
      path: `${DRAFTS_DIR}/${draftFileName(draft.draftId)}`,
      data: JSON.stringify(draft),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  } catch (err) {
    console.warn("[native-mirror] mirrorDraft failed (non-fatal):", err);
  }
}

/**
 * Drop a draft's mirror once the IndexedDB row is cleared — submitted, or the
 * legacy un-partitioned draft evicted on job-scoped open. Keeps the mirror in
 * lockstep so a stale draft can't resurrect after submission.
 */
export async function removeMirroredDraft(draftId: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory } = await fsModule();
    await Filesystem.deleteFile({
      path: `${DRAFTS_DIR}/${draftFileName(draftId)}`,
      directory: Directory.Data,
    }).catch(() => {});
  } catch (err) {
    console.warn("[native-mirror] removeMirroredDraft failed (non-fatal):", err);
  }
}

// ── Audio write path ─────────────────────────────────────────────────────────

/**
 * Mirror an audio session's metadata to the native sandbox. Re-mirrored on every
 * meaningful transition (create → server-id assigned → pending-finalize) so the
 * persisted `serverSessionId` and `status` stay current; without the
 * serverSessionId a restored ACTIVE session would create a *duplicate* server
 * session on the next drain. Terminal sessions are never mirrored — they're
 * being torn down, and `removeMirroredAudioSession` clears any prior copy.
 */
export async function mirrorAudioSession(session: LocalAudioSession): Promise<void> {
  if (!isNative()) return;
  if (session.status === "FINALIZED" || session.status === "DISCARDED") {
    await removeMirroredAudioSession(session.localSessionId);
    return;
  }
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    await ensureDir(Filesystem, Directory, AUDIO_SESS_DIR);
    await Filesystem.writeFile({
      path: `${AUDIO_SESS_DIR}/${session.localSessionId}.json`,
      data: JSON.stringify(session),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  } catch (err) {
    console.warn("[native-mirror] mirrorAudioSession failed (non-fatal):", err);
  }
}

/**
 * Mirror an audio chunk's raw blob (+ sidecar metadata) to the native sandbox.
 * This is the highest-value capture to protect — a long offline site recording
 * lives only as PENDING chunk blobs in IndexedDB until upload. A chunk with no
 * blob (already uploaded, row kept only for bookkeeping) has nothing at risk, so
 * we skip it.
 */
export async function mirrorAudioChunk(chunk: LocalAudioChunk): Promise<void> {
  if (!isNative()) return;
  if (!chunk.blob) return;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    await ensureDir(Filesystem, Directory, AUDIO_CHUNK_DIR);
    const id = chunk.localChunkId;
    const base64 = await blobToBase64(chunk.blob);
    await Filesystem.writeFile({
      path: `${AUDIO_CHUNK_DIR}/${id}.bin`,
      data: base64, // no encoding ⇒ written as base64 binary
      directory: Directory.Data,
    });
    const meta = {
      localChunkId: id,
      localSessionId: chunk.localSessionId,
      sequenceIndex: chunk.sequenceIndex,
      startSec: chunk.startSec,
      durationSec: chunk.durationSec,
      mimeType: chunk.mimeType,
      filename: chunk.filename,
      createdAt: chunk.createdAt,
    };
    await Filesystem.writeFile({
      path: `${AUDIO_CHUNK_DIR}/${id}.meta.json`,
      data: JSON.stringify(meta),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  } catch (err) {
    console.warn("[native-mirror] mirrorAudioChunk failed (non-fatal):", err);
  }
}

// ── Audio cleanup path ───────────────────────────────────────────────────────

/** Drop a single chunk's mirror once it's uploaded (server has the audio). */
export async function removeMirroredAudioChunk(localChunkId: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory } = await fsModule();
    const del = (path: string) =>
      Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
    await del(`${AUDIO_CHUNK_DIR}/${localChunkId}.bin`);
    await del(`${AUDIO_CHUNK_DIR}/${localChunkId}.meta.json`);
  } catch (err) {
    console.warn("[native-mirror] removeMirroredAudioChunk failed (non-fatal):", err);
  }
}

/**
 * Drop a session's mirror plus every chunk mirror belonging to it. Called when
 * the IndexedDB session row is torn down (finalize/discard/purge) — the queue
 * bulk-deletes chunk rows there without firing per-chunk delete hooks, so we
 * scan the mirrored chunk metas to find this session's chunks ourselves.
 */
export async function removeMirroredAudioSession(localSessionId: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    const del = (path: string) =>
      Filesystem.deleteFile({ path, directory: Directory.Data }).catch(() => {});
    await del(`${AUDIO_SESS_DIR}/${localSessionId}.json`);
    for (const file of await listDir(Filesystem, Directory, AUDIO_CHUNK_DIR)) {
      if (!file.endsWith(".meta.json")) continue;
      const id = file.slice(0, -".meta.json".length);
      try {
        const metaRaw = await Filesystem.readFile({
          path: `${AUDIO_CHUNK_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const meta = JSON.parse(metaRaw.data as string);
        if (meta.localSessionId !== localSessionId) continue;
      } catch {
        continue; // unreadable meta — leave it for the next sweep
      }
      await del(`${AUDIO_CHUNK_DIR}/${id}.bin`);
      await del(`${AUDIO_CHUNK_DIR}/${id}.meta.json`);
    }
  } catch (err) {
    console.warn("[native-mirror] removeMirroredAudioSession failed (non-fatal):", err);
  }
}

// ── Recovery path ────────────────────────────────────────────────────────────

async function listDir(
  Filesystem: typeof import("@capacitor/filesystem").Filesystem,
  Directory: typeof import("@capacitor/filesystem").Directory,
  path: string,
): Promise<string[]> {
  try {
    const { files } = await Filesystem.readdir({ path, directory: Directory.Data });
    return files.map((f) => (typeof f === "string" ? f : f.name));
  } catch {
    return []; // dir doesn't exist yet
  }
}

/**
 * Restore any mirrored records that IndexedDB is missing (eviction recovery).
 * Runs on app startup BEFORE the first sync drain. No-op on web. Returns counts
 * for logging/telemetry.
 */
export async function rehydrateFromMirror(): Promise<{
  submissions: number;
  attachments: number;
}> {
  const restored = { submissions: 0, attachments: 0 };
  if (!isNative()) return restored;

  try {
    const { Filesystem, Directory, Encoding } = await fsModule();

    // Attachments first, so a restored submission's blobs are already present.
    for (const file of await listDir(Filesystem, Directory, ATT_DIR)) {
      if (!file.endsWith(".meta.json")) continue;
      const id = file.slice(0, -".meta.json".length);
      if (await offlineDb.queuedAttachments.get(id)) continue; // intact

      try {
        const metaRaw = await Filesystem.readFile({
          path: `${ATT_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const meta = JSON.parse(metaRaw.data as string);
        const binRaw = await Filesystem.readFile({
          path: `${ATT_DIR}/${id}.bin`,
          directory: Directory.Data,
        });
        const attachment = {
          localAttachmentId: id,
          localSubmissionId: meta.localSubmissionId,
          filename: meta.filename,
          mimeType: meta.mimeType,
          size: meta.size,
          blob: base64ToBlob(binRaw.data as string, meta.mimeType),
          uploadedRemoteKey: null,
          createdAt: meta.createdAt,
          formUrl: meta.formUrl,
          fieldId: meta.fieldId,
          jobId: meta.jobId ?? null,
        } as MirroredAttachment;
        await offlineDb.queuedAttachments.put(attachment);
        restored.attachments++;
      } catch (err) {
        console.warn(`[native-mirror] could not restore attachment ${id}:`, err);
      }
    }

    for (const file of await listDir(Filesystem, Directory, SUBS_DIR)) {
      if (!file.endsWith(".json")) continue;
      const id = file.slice(0, -".json".length);
      if (await offlineDb.queuedSubmissions.get(id)) continue; // intact

      try {
        const raw = await Filesystem.readFile({
          path: `${SUBS_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const sub = JSON.parse(raw.data as string) as QueuedSubmission;
        // Re-arm for sync: never trust a persisted "synced"/"syncing" status on
        // a restored row — force it back through the drain + reconcile path.
        sub.status = "queued";
        sub.syncedAt = null;
        sub.serverConfirmedAt = null;
        await offlineDb.queuedSubmissions.put(sub);
        restored.submissions++;
      } catch (err) {
        console.warn(`[native-mirror] could not restore submission ${id}:`, err);
      }
    }

    if (restored.submissions || restored.attachments) {
      console.info(
        `[native-mirror] recovered ${restored.submissions} submission(s) + ${restored.attachments} attachment(s) from native storage after eviction`,
      );
    }
  } catch (err) {
    console.warn("[native-mirror] rehydrate failed (non-fatal):", err);
  }

  return restored;
}

/**
 * Restore any mirrored form drafts that IndexedDB is missing (eviction
 * recovery), so a half-filled form the tech left open comes back after the
 * WKWebView evicted IndexedDB — not just after an ordinary close/reopen. Runs on
 * app startup; the attachment rehydrate above has already restored any
 * `local://attach_` blobs the draft references. No-op on web.
 */
export async function rehydrateDraftsFromMirror(): Promise<number> {
  if (!isNative()) return 0;
  let restored = 0;
  try {
    const { Filesystem, Directory, Encoding } = await fsModule();
    for (const file of await listDir(Filesystem, Directory, DRAFTS_DIR)) {
      if (!file.endsWith(".json")) continue;
      try {
        const raw = await Filesystem.readFile({
          path: `${DRAFTS_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const draft = JSON.parse(raw.data as string) as FormDraft;
        if (!draft?.draftId) continue;
        if (await offlineDb.formDrafts.get(draft.draftId)) continue; // intact
        await offlineDb.formDrafts.put(draft);
        restored++;
      } catch (err) {
        console.warn(`[native-mirror] could not restore draft ${file}:`, err);
      }
    }
    if (restored) {
      console.info(
        `[native-mirror] recovered ${restored} form draft(s) from native storage after eviction`,
      );
    }
  } catch (err) {
    console.warn("[native-mirror] draft rehydrate failed (non-fatal):", err);
  }
  return restored;
}

/**
 * Restore any mirrored audio sessions/chunks that IndexedDB is missing (eviction
 * recovery). Sessions are restored BEFORE chunks because the sync loop walks
 * active sessions and then drains each one's pending chunks — an orphaned chunk
 * with no session would never be picked up. Runs on app startup before the first
 * audio drain. No-op on web. Returns counts for logging/telemetry.
 */
export async function rehydrateAudioFromMirror(): Promise<{
  sessions: number;
  chunks: number;
}> {
  const restored = { sessions: 0, chunks: 0 };
  if (!isNative()) return restored;

  try {
    const { Filesystem, Directory, Encoding } = await fsModule();

    // Sessions first, so a restored chunk has a session to belong to.
    for (const file of await listDir(Filesystem, Directory, AUDIO_SESS_DIR)) {
      if (!file.endsWith(".json")) continue;
      const id = file.slice(0, -".json".length);
      if (await offlineDb.audioSessions.get(id)) continue; // intact

      try {
        const raw = await Filesystem.readFile({
          path: `${AUDIO_SESS_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const session = JSON.parse(raw.data as string) as LocalAudioSession;
        // Don't resurrect a session that already reached a terminal state.
        if (session.status === "FINALIZED" || session.status === "DISCARDED") {
          continue;
        }
        // Keep status + serverSessionId verbatim so the drain resumes exactly
        // where it left off (create / upload chunks / finalize); just clear any
        // stale error from before the eviction.
        session.lastError = null;
        await offlineDb.audioSessions.put(session);
        restored.sessions++;
      } catch (err) {
        console.warn(`[native-mirror] could not restore audio session ${id}:`, err);
      }
    }

    for (const file of await listDir(Filesystem, Directory, AUDIO_CHUNK_DIR)) {
      if (!file.endsWith(".meta.json")) continue;
      const id = file.slice(0, -".meta.json".length);
      if (await offlineDb.audioChunks.get(id)) continue; // intact

      try {
        const metaRaw = await Filesystem.readFile({
          path: `${AUDIO_CHUNK_DIR}/${file}`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        const meta = JSON.parse(metaRaw.data as string);
        const binRaw = await Filesystem.readFile({
          path: `${AUDIO_CHUNK_DIR}/${id}.bin`,
          directory: Directory.Data,
        });
        const chunk: LocalAudioChunk = {
          localChunkId: id,
          localSessionId: meta.localSessionId,
          sequenceIndex: meta.sequenceIndex,
          startSec: meta.startSec,
          durationSec: meta.durationSec,
          mimeType: meta.mimeType,
          filename: meta.filename,
          blob: base64ToBlob(binRaw.data as string, meta.mimeType),
          // Re-arm for upload: never trust a persisted UPLOADING/FAILED state,
          // and reset retries so an evicted-then-restored chunk isn't born dead.
          status: "PENDING",
          retryCount: 0,
          createdAt: meta.createdAt,
          lastError: null,
        };
        await offlineDb.audioChunks.put(chunk);
        restored.chunks++;
      } catch (err) {
        console.warn(`[native-mirror] could not restore audio chunk ${id}:`, err);
      }
    }

    if (restored.sessions || restored.chunks) {
      console.info(
        `[native-mirror] recovered ${restored.sessions} audio session(s) + ${restored.chunks} chunk(s) from native storage after eviction`,
      );
    }
  } catch (err) {
    console.warn("[native-mirror] audio rehydrate failed (non-fatal):", err);
  }

  return restored;
}

// ── Durability probe ─────────────────────────────────────────────────────────

/**
 * Is the native durability mirror actually LIVE on this device?
 *
 * This is the honest answer to "can unsynced work survive here?" on the
 * Capacitor app. `navigator.storage.persist()` is essentially always DENIED in
 * WKWebView, so a UI that trusts it alone screams "your work may be lost" on
 * exactly the devices where this mirror is quietly keeping every queued
 * submission, draft, attachment and audio chunk in the OS sandbox — a false
 * alarm, fired hardest where it's least true. The banner state machine
 * (./banner-state.ts) therefore counts a live mirror as durable, which means
 * this must only ever return `true` when the mirror really can write: native
 * platform AND the @capacitor/filesystem plugin reachable AND Directory.Data
 * usable.
 *
 * We prove all three rather than infer them: mkdir the mirror root (an
 * unregistered plugin throws here), then readdir it via the RAW plugin call —
 * not the swallow-everything `listDir` helper — so a broken bridge surfaces as
 * `false` rather than a confident lie. Cheap; callers cache the result.
 */
export async function isNativeMirrorActive(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const { Filesystem, Directory } = await fsModule();
    await ensureDir(Filesystem, Directory, ROOT);
    await Filesystem.readdir({ path: ROOT, directory: Directory.Data });
    return true;
  } catch (err) {
    console.warn(
      "[native-mirror] durability probe failed — treating this device as un-mirrored:",
      err,
    );
    return false;
  }
}

/**
 * Delete the ENTIRE mirror. Explicit user action only — never call this from a
 * sync, cleanup or eviction path.
 *
 * This exists because "delete my data from this device" was a lie without it,
 * and not a harmless one: the mirror lives in the OS sandbox, outside every
 * browser storage API, so clearing IndexedDB leaves it untouched. Worse, the
 * rehydrate-on-boot path (`rehydrateFromMirror` and friends) would then read it
 * back and the "deleted" photos, drafts and audio would simply reappear on the
 * next launch, which reads as a bug and is a privacy failure when a tablet
 * changes hands.
 *
 * `recursive: true` takes the root and everything under it in one call. A
 * missing directory is success, not failure: there is nothing to delete.
 */
export async function clearNativeMirror(): Promise<void> {
  if (!isNative()) return;
  try {
    const { Filesystem, Directory } = await fsModule();
    await Filesystem.rmdir({ path: ROOT, directory: Directory.Data, recursive: true });
  } catch (err) {
    // Already gone is the common case and indistinguishable from a real failure
    // through this API, so don't fail the wipe over it — but say so, because a
    // genuine failure means unsynced work survived a deletion the user asked for.
    console.warn("[native-mirror] clear failed (or nothing to clear):", err);
  }
}
